import React from 'react';
import { Typography, Box, List, ListItem, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import LegalLayout from './LegalLayout';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      name: 'Задължителни cookies',
      purpose: 'Необходими за функционирането на сайта',
      examples: 'Сесийни cookies, настройки за език',
      duration: 'Сесия или до 1 година'
    },
    {
      name: 'Функционални cookies',
      purpose: 'Запомнят Вашите предпочитания',
      examples: 'Продукти в количката, потребителски настройки',
      duration: 'До 2 години'
    },
    {
      name: 'Аналитични cookies',
      purpose: 'Помагат ни да подобряваме сайта',
      examples: 'Google Analytics, статистики за посещения',
      duration: 'До 2 години'
    },
    {
      name: 'Маркетингови cookies',
      purpose: 'Персонализират рекламите',
      examples: 'Facebook Pixel, Google Ads',
      duration: 'До 1 година'
    }
  ];

  return (
    <LegalLayout title="Политика за cookies">
      <Typography paragraph>
        Този документ обяснява как goldenbooks.bg използва cookies и подобни 
        технологии за подобряване на Вашето потребителско изживяване.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        1. Какво са cookies?
      </Typography>
      <Typography paragraph>
        Cookies са малки текстови файлове, които се съхраняват на Вашето устройство 
        (компютър, таблет, телефон) когато посещавате даден уебсайт. Те помагат на 
        сайта да "запомни" информация за Вашето посещение, като предпочитания за 
        език или съдържанието на количката Ви.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        2. Защо използваме cookies?
      </Typography>
      <Typography paragraph>
        Използваме cookies за да:
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Осигурим правилното функциониране на сайта" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Запомним Вашите предпочитания и настройки" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Анализираме как потребителите използват сайта" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Подобрим потребителското изживяване" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Показваме релевантни реклами" />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        3. Видове cookies
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Тип</strong></TableCell>
              <TableCell><strong>Предназначение</strong></TableCell>
              <TableCell><strong>Примери</strong></TableCell>
              <TableCell><strong>Продължителност</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cookieTypes.map((cookie, index) => (
              <TableRow key={index}>
                <TableCell>{cookie.name}</TableCell>
                <TableCell>{cookie.purpose}</TableCell>
                <TableCell>{cookie.examples}</TableCell>
                <TableCell>{cookie.duration}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        4. Първични и трети страни cookies
      </Typography>
      <Typography paragraph>
        <strong>Първични cookies</strong> се задават директно от нашия домейн 
        (goldenbooks.bg) и се използват за основните функции на сайта.
      </Typography>
      <Typography paragraph>
        <strong>Трети страни cookies</strong> се задават от външни услуги като:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="Google Analytics" 
            secondary="За статистики и анализ на трафика" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Google Ads" 
            secondary="За показване на релевантни реклами" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Facebook Pixel" 
            secondary="За маркетингови цели в социалните мрежи" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Платежни системи" 
            secondary="За сигурно обработване на плащанията" 
          />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        5. Как да управлявате cookies?
      </Typography>
      <Typography paragraph>
        Можете да контролирате и управлявате cookies по следните начини:
      </Typography>

      <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
        Чрез браузъра:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="Chrome: Настройки > Поверителност и сигурност > Cookies" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Firefox: Настройки > Поверителност и сигурност" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Safari: Предпочитания > Поверителност" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Edge: Настройки > Cookies и разрешения за сайт" 
          />
        </ListItem>
      </List>

      <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
        Чрез нашия сайт:
      </Typography>
      <Typography paragraph>
        При първото Ви посещение ще видите банер за cookies, където можете да 
        изберете кои типове cookies да приемете. Можете да промените настройките 
        си по всяко време чрез линка в долната част на сайта.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        6. Последствия от отказването на cookies
      </Typography>
      <Typography paragraph>
        Ако откажете определени cookies, това може да повлияе на функционалността на сайта:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="Задължителни cookies" 
            secondary="Без тях сайтът няма да функционира правилно" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Функционални cookies" 
            secondary="Няма да запомним Вашите предпочитания" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Аналитични cookies" 
            secondary="Няма да можем да подобряваме сайта ефективно" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Маркетингови cookies" 
            secondary="Рекламите може да не бъдат релевантни за Вас" 
          />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        7. Актуализации на политиката
      </Typography>
      <Typography paragraph>
        Тази политика за cookies може да бъде актуализирана периодично за да 
        отразява промени в нашите практики или в законодателството. 
        Препоръчваме редовно да проверявате тази страница за последните актуализации.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        8. Контакт
      </Typography>
      <Typography paragraph>
        Ако имате въпроси относно нашата употреба на cookies, моля свържете се с нас:
      </Typography>
      <Typography paragraph>
        Имейл: info@goldenbooks.bg<br />
        Телефон: 0895628293
      </Typography>
    </LegalLayout>
  );
};

export default CookiePolicy;